import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, MailCheck } from 'lucide-react'
import { toast } from 'sonner'
import { KaryvaLogoAnimated3D } from '../components/KaryvaLogoAnimated3D'
import { useTranslation } from '../i18n/I18nContext'
import { passwordResetService } from '../services/passwordReset'

/**
 * Sprint 29 — public "Forgot password?" page.
 *
 * Always shows the same success state after submitting: the backend
 * returns 202 whether or not the email exists (anti-enumeration), so the
 * UI must not reveal anything either.
 */
export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await passwordResetService.requestReset(email)
      setSent(true)
    } catch {
      // Network/server failure only — a 202 always resolves.
      toast.error(t('auth.reset.genericError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#102a43] via-[#1a3a52] to-[#102a43] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <KaryvaLogoAnimated3D size="header" darkBg={true} />
        </div>

        <div className="bg-[#1d293d] rounded-[14px] border border-[#314158] p-6 md:p-8 shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.3)]">
          {sent ? (
            <div className="space-y-4 text-center">
              <MailCheck className="size-10 text-[#DAA520] mx-auto" />
              <h1 className="font-['Arial:Regular',sans-serif] text-xl text-white">
                {t('auth.reset.sentTitle')}
              </h1>
              <p className="font-['Arial:Regular',sans-serif] text-sm leading-[22px] text-[#cad5e2]">
                {t('auth.reset.sentBody')}
              </p>
              <Link
                to="/login"
                className="inline-block text-sm text-[#51a2ff] hover:text-[#73b5ff] transition-colors"
              >
                {t('auth.reset.backToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2 text-center">
                <h1 className="font-['Arial:Regular',sans-serif] text-xl text-white">
                  {t('auth.reset.title')}
                </h1>
                <p className="font-['Arial:Regular',sans-serif] text-sm leading-[22px] text-[#90a1b9]">
                  {t('auth.reset.subtitle')}
                </p>
              </div>

              <div className="space-y-2">
                <label className="block" htmlFor="reset-email">
                  <span className="font-['Arial:Regular',sans-serif] text-sm text-[#cad5e2]">
                    {t('auth.reset.emailLabel')}
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Mail className="size-4 text-[#62748e]" />
                  </div>
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    required
                    autoFocus
                    className="w-full bg-[rgba(15,23,43,0.5)] h-[40px] rounded-[8px] pl-10 pr-3 py-2 font-['Arial:Regular',sans-serif] text-sm text-white placeholder-[#62748e] border border-[#45556c] focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#fe9a00] to-[#e17100] h-[44px] rounded-[8px] shadow-[0px_10px_15px_-3px_rgba(123,51,6,0.3)] hover:from-[#e58900] hover:to-[#c86600] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <span className="font-['Arial:Regular',sans-serif] text-sm text-[#0f172b] font-medium">
                  {loading ? t('auth.reset.sending') : t('auth.reset.sendLink')}
                </span>
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-[#51a2ff] hover:text-[#73b5ff] transition-colors"
                >
                  {t('auth.reset.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
